import { useCallback } from 'react';
import { useQuery } from '@apollo/client';
import {
  ACTIVE_ONBOARDING_STEPS,
  GLOBAL_SHELL_EVENTS,
  TOTAL_ONBOARDING_STEPS,
  useGlobalShellEvents,
} from '@robinpowered/dashboard-apps-common';
import { GetCompletedOnboardingStepsForDashboardShell } from '../graphql/GetCompletedOnboardingStepsForDashboardShell';
import { useApolloContext } from '../contexts';

type GetCompletedOnboardingStepsData = {
  getCompletedOnboardingStepsForOrg?: string[] | null;
};

export type OnboardingProgress = {
  completedCount: number;
  totalSteps: number;
  allComplete: boolean;
};

/**
 * Returns the current onboarding step progress for use in the nav sidebar.
 *
 * Data comes from a GQL query. When the setup app dispatches
 * `dashboard:onboarding-steps-updated` (after steps change), the query is
 * refetched so the badge stays in sync.
 *
 * Returns `null` until the initial query resolves.
 */
export function useOnboardingProgress(): OnboardingProgress | null {
  const { tenantId } = useApolloContext();
  const { data, loading, refetch } = useQuery<GetCompletedOnboardingStepsData>(
    GetCompletedOnboardingStepsForDashboardShell,
    {
      skip: !tenantId,
    }
  );

  const handleStepsUpdated = useCallback(() => {
    void refetch();
  }, [refetch]);

  useGlobalShellEvents(
    GLOBAL_SHELL_EVENTS.ONBOARDING_STEPS_UPDATED,
    handleStepsUpdated
  );

  if (loading && data?.getCompletedOnboardingStepsForOrg == null) {
    return null;
  }

  const completedCount =
    data?.getCompletedOnboardingStepsForOrg?.filter((step) =>
      (ACTIVE_ONBOARDING_STEPS as readonly string[]).includes(step)
    ).length ?? 0;

  return {
    completedCount,
    totalSteps: TOTAL_ONBOARDING_STEPS,
    allComplete: completedCount >= TOTAL_ONBOARDING_STEPS,
  };
}
